import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { formatRelative } from "../lib/utils";

export function DocsList() {
  const search = useSearch({ strict: false }) as {
    project?: string;
    kind?: string;
    lang?: string;
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ["docs", search],
    queryFn: () => api.listDocs(search),
  });

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          Documentos {search.project ? `· ${search.project}` : ""}
        </h2>
        <div className="text-sm text-(--text-tertiary)">
          {data ? `${data.items.length} resultados` : ""}
        </div>
      </header>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-(--error) bg-(--error)/10 p-4 text-sm">
          Error cargando documentos: {String(error)}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-(--border-strong) p-8 text-center">
          <p className="text-(--text-secondary)">No hay documentos todavía.</p>
          <Link to="/upload" className="mt-2 inline-block text-(--accent-500) hover:underline">
            Importa tu primer documento →
          </Link>
        </div>
      )}

      <ul className="space-y-2">
        {data?.items.map((item) => (
          <li
            key={item.id}
            className="rounded-md border border-(--border-subtle) bg-(--bg-elevated) px-3 py-2 hover:bg-(--bg-hover)"
          >
            <Link to="/docs/$docId" params={{ docId: item.id }} className="block">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{item.title}</div>
                  <div className="text-xs text-(--text-tertiary)">
                    {item.project} · {item.kind ?? "—"} · {item.lang}
                    {item.owner_subject ? ` · ${item.owner_subject}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {item.tags.slice(0, 3).map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px]">
                      #{t}
                    </Badge>
                  ))}
                  <span className="text-[10px] text-(--text-tertiary)">
                    {formatRelative(item.updated_at)}
                  </span>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
